import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { message } from "antd";
import moment from 'moment';

import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import useApplicationsData from './useApplicationsData';
import MobileSheet from './MobileSheet';
import { formatKes, formatTerm, initialsOf, pluralize } from './format';
import './MobileApplications.css';

/* Chip labels are LoanApplication.Status's own labels. A status with
   no rows has no status_name to read, so they can't come from the data. */
const FILTERS = [
    { key: "all", label: "All" },
    { key: "pending", label: "Pending", status: "PENDING" },
    { key: "approved", label: "Approved", status: "APPROVED" },
    { key: "declined", label: "Declined", status: "DECLINED" },
    { key: "withdrawn", label: "Withdrawn", status: "WITHDRAWN" },
];

// Pill colours keyed by the status enum, never by label text.
// DECLINED and WITHDRAWN take the neutral base pill.
const PILL_CLASS = {
    PENDING: "pending",
    APPROVED: "approved",
};

const SORTS = [
    { key: "newest", label: "Newest" },
    { key: "oldest", label: "Oldest" },
    { key: "amount", label: "Amount: High to Low" },
    { key: "customer", label: "Customer A–Z" },
];

const compareBy = {
    newest: (a, b) => new Date(b.date) - new Date(a.date),
    oldest: (a, b) => new Date(a.date) - new Date(b.date),
    amount: (a, b) => b.amount - a.amount,
    customer: (a, b) => a.customerName.localeCompare(b.customerName),
};

const SearchIcon = () => (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
        <circle cx="8.5" cy="8.5" r="5.5" stroke="#8fa7bd" strokeWidth="2" />
        <line x1="13" y1="13" x2="17.5" y2="17.5" stroke="#8fa7bd" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

const MobileApplications = () => {
    const navigate = useNavigate();
    const { isLoading, isError, refetch, counts, applications } = useApplicationsData();

    const [filter, setFilter] = useState("all");
    const [sort, setSort] = useState("newest");
    const [sortOpen, setSortOpen] = useState(false);
    const [search, setSearch] = useState("");

    const filterStatus = FILTERS.find(option => option.key === filter).status;

    const query = search.trim().toLowerCase().replace(/\s+/g, '');
    const visible = applications
        .filter(application => !filterStatus || application.status === filterStatus)
        .filter(application =>
            !query ||
            (application.reference || '').toLowerCase().replace(/\s+/g, '').includes(query) ||
            application.customerName.toLowerCase().replace(/\s+/g, '').includes(query)
        )
        .sort(compareBy[sort]);

    return (
        <div className="mobile-applications">
            <header className="mobile-applications-header">
                <div className="mobile-applications-title-row">
                    <div className="mobile-applications-title-left">
                        <button
                            type="button"
                            className="mobile-applications-back"
                            aria-label="Back"
                            onClick={() => navigate('/dashboard/stats')}
                        >
                            <ArrowBackIosNewIcon className="mobile-applications-back-icon" />
                        </button>
                        <h1 className="mobile-applications-title">Applications</h1>
                    </div>

                    {/* Gated until the mobile new-application flow is designed. */}
                    <button
                        type="button"
                        className="mobile-applications-add"
                        aria-label="New application"
                        onClick={() => message.info('Coming soon')}
                    >
                        <AddIcon className="mobile-applications-add-icon" />
                    </button>
                </div>

                <label className="mobile-applications-search">
                    <SearchIcon />
                    <input
                        type="search"
                        value={search}
                        onChange={event => setSearch(event.target.value)}
                        placeholder="Search reference or customer"
                    />
                </label>

                <div className="mobile-applications-chips">
                    {FILTERS.map(({ key, label }) => (
                        <button
                            type="button"
                            key={key}
                            className={[
                                "mobile-applications-chip",
                                key === "pending" ? "pending" : "",
                                key === filter ? "selected" : "",
                            ].join(' ')}
                            onClick={() => setFilter(key)}
                        >
                            {counts ? `${label} · ${counts[key]}` : label}
                        </button>
                    ))}
                </div>
            </header>

            <div className="mobile-applications-list">
                <div className="mobile-applications-meta">
                    <span className="mobile-applications-count">
                        {!isLoading && !isError && pluralize(visible.length, "application")}
                    </span>
                    <button
                        type="button"
                        className="mobile-applications-sort-trigger"
                        onClick={() => setSortOpen(true)}
                    >
                        Sort: {SORTS.find(option => option.key === sort).label}
                        <KeyboardArrowDownIcon className="mobile-applications-sort-caret" />
                    </button>
                </div>

                <div className="mobile-applications-card">
                    {isLoading && [1, 2, 3, 4, 5].map(n => (
                        <div className="mobile-applications-row" key={n}>
                            <span className="mobile-applications-avatar" />
                            <span className="mobile-applications-text">
                                <span className="mobile-applications-skeleton wide" />
                                <span className="mobile-applications-skeleton" />
                            </span>
                        </div>
                    ))}

                    {isError && (
                        <div className="mobile-applications-error">
                            Couldn't load applications
                            <button
                                type="button"
                                className="mobile-applications-retry"
                                onClick={() => refetch()}
                            >
                                Retry
                            </button>
                        </div>
                    )}

                    {!isLoading && !isError && visible.length === 0 && (
                        <div className="mobile-applications-empty">No applications match</div>
                    )}

                    {!isLoading && !isError && visible.map(application => (
                        <Link
                            to={`/dashboard/application/${application.id}`}
                            className="mobile-applications-row"
                            key={application.id}
                        >
                            <span className="mobile-applications-avatar">{initialsOf(application.customerName)}</span>
                            <span className="mobile-applications-text">
                                <span className="mobile-applications-name">{application.customerName}</span>
                                <span className="mobile-applications-sub">
                                    {application.reference} · {application.loanType}
                                </span>
                                <span className="mobile-applications-sub muted">
                                    {moment(application.date).format('DD-MM-YYYY')} · {formatTerm(application.term, application.termUnitName)}
                                </span>
                            </span>
                            <span className="mobile-applications-right">
                                <span className="mobile-applications-amount">{formatKes(application.amount)}</span>
                                <span className={`mobile-applications-pill ${PILL_CLASS[application.status] || ""}`}>
                                    {application.statusDisplay}
                                </span>
                            </span>
                        </Link>
                    ))}
                </div>
            </div>

            <MobileSheet
                open={sortOpen}
                onClose={() => setSortOpen(false)}
                title="Sort by"
                options={SORTS}
                selected={sort}
                onSelect={setSort}
            />
        </div>
    );
};

export default MobileApplications;
